import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  useColorMode,
  keyframes,
} from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import type { ReactElement } from "react";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface StepCardProps {
  icon: ReactElement;
  titlePreface: string;
  title: string;
  paragraphs: string[];
  isLast?: boolean;
}

export default function StepCard({
  icon,
  titlePreface,
  title,
  paragraphs,
  isLast = false,
}: StepCardProps) {
  const { colorMode } = useColorMode();
  const shouldReduceMotion = useReducedMotion();

  return (
    <Box w="full" position="relative" overflow="hidden">
      {/* Subtle background pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.5}
        bgImage={
          colorMode === "light"
            ? "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)"
            : "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)"
        }
        bgSize="40px 40px"
      />

      <Container maxW="6xl" position="relative" zIndex={1}>
        <Flex
          direction={{ base: "column", lg: "row" }}
          align="center"
          justify="center"
          gap={{ base: 8, lg: 16 }}
          py={{ base: 20, md: 28 }}
        >
          {/* Left side - Step number and icon */}
          <VStack spacing={6}>
            {/* Step number badge */}
            <Box
              w={20}
              h={20}
              borderRadius="2xl"
              bg={colorMode === "light" ? "white" : "rgba(255, 255, 255, 0.05)"}
              border="1px solid"
              borderColor={
                colorMode === "light" ? "neutral.100" : "neutral.800"
              }
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              boxShadow={
                colorMode === "light"
                  ? "0 10px 40px rgba(0, 0, 0, 0.08)"
                  : "0 10px 40px rgba(0, 0, 0, 0.3)"
              }
              animation={
                shouldReduceMotion ? undefined : `${fadeInUp} 0.6s ease-out`
              }
            >
              <Box color="brand.500">{icon}</Box>
            </Box>

            {/* Connecting line (not on last item) */}
            {!isLast && (
              <Box
                display={{ base: "none", lg: "block" }}
                w="2px"
                h="100px"
                bgGradient="linear(to-b, brand.500, transparent)"
                opacity={0.3}
              />
            )}
          </VStack>

          {/* Right side - Content */}
          <VStack
            align={{ base: "center", lg: "flex-start" }}
            spacing={4}
            textAlign={{ base: "center", lg: "left" }}
          >
            <Text
              color="brand.500"
              fontSize="sm"
              fontWeight={700}
              letterSpacing="0.15em"
              textTransform="uppercase"
            >
              {titlePreface}
            </Text>

            <Heading
              color={
                colorMode === "light"
                  ? "text.light-primary"
                  : "text.dark-primary"
              }
              fontWeight={800}
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              lineHeight="1.2"
              letterSpacing="-0.02em"
            >
              {title}
            </Heading>

            <VStack
              align={{ base: "center", lg: "flex-start" }}
              spacing={4}
              pt={2}
            >
              {paragraphs.map((paragraph) => (
                <Text
                  key={paragraph.substring(0, 50)}
                  color={
                    colorMode === "light"
                      ? "text.light-secondary"
                      : "text.dark-muted"
                  }
                  fontSize={{ base: "md", md: "lg" }}
                  lineHeight="1.8"
                  maxW="2xl"
                >
                  {paragraph}
                </Text>
              ))}
            </VStack>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
}
